import { AlertTriangle, Check, X } from 'lucide-react'
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'

type NotificationKind = 'error' | 'success'

type Notification = {
  id: number
  kind: NotificationKind
  message: string
}

type NotificationsValue = {
  notify: (message: string, kind?: NotificationKind) => void
}

const NotificationsContext = createContext<NotificationsValue | null>(null)

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([])

  const dismiss = useCallback((id: number) => {
    setNotifications((current) => current.filter((item) => item.id !== id))
  }, [])

  const notify = useCallback(
    (message: string, kind: NotificationKind = 'error') => {
      const id = Date.now() + Math.random()
      setNotifications((current) => [...current.slice(-2), { id, kind, message }])
      window.setTimeout(() => dismiss(id), 6_000)
    },
    [dismiss],
  )

  const value = useMemo(() => ({ notify }), [notify])

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <div className="notification-stack" role="region" aria-label="Notifications">
        {notifications.map((notification) => (
          <div
            className={`notification notification-${notification.kind}`}
            role={notification.kind === 'error' ? 'alert' : 'status'}
            key={notification.id}
          >
            {notification.kind === 'error' ? <AlertTriangle /> : <Check />}
            <span>{notification.message}</span>
            <button
              type="button"
              onClick={() => dismiss(notification.id)}
              aria-label="Dismiss notification"
            >
              <X />
            </button>
          </div>
        ))}
      </div>
    </NotificationsContext.Provider>
  )
}

// Hooks and their provider intentionally share this small notification boundary.
// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications() {
  const value = useContext(NotificationsContext)
  if (!value) throw new Error('NotificationsProvider is missing')
  return value
}
